import React, {
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
    Box,
    Card,
    Stack,
    Typography,
    useTheme,
    Avatar,
    Paper,
} from "@mui/material"; // CHANGED: add Paper
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Button from "../Button";
import { useNavigate } from "react-router-dom";
import { CARD_STYLES } from "../../constants/styles";

interface SetupWizardProps {
    brandConfigured: boolean;
    wallpaperConfigured: boolean;
    servicesConfigured: boolean;
    locationConfigured: boolean;
    profileConfigured: boolean;
    loyaltyConfigured: boolean;
}

const SetupWizard = ({
    brandConfigured,
    wallpaperConfigured,
    servicesConfigured,
    locationConfigured,
    profileConfigured,
    loyaltyConfigured,
}: SetupWizardProps) => {
    const theme = useTheme();
    const navigate = useNavigate();

    const ONBOARDING_STEPS = useMemo(
        () => [
            {
                key: "brand",
                label: "Set up color and logo",
                explainText:
                    "Let’s give your app a personal touch with your logo and favorite color.",
                successText: "Your brand is now shining with its unique style!",
                isDone: brandConfigured,
                onClick: () => navigate("/menu/brand"),
            },
            {
                key: "wallpaper",
                label: "Choose wallpaper",
                explainText:
                    "Pick a welcoming background that sets the vibe for your space.",
                successText: "A beautiful wallpaper is now live in your app.",
                isDone: wallpaperConfigured,
                onClick: () => navigate("/menu/wallpaper"),
            },
            {
                key: "services",
                label: "Add services & prices",
                explainText:
                    "Tell clients what you offer and how much it costs — make it easy to choose you.",
                successText:
                    "Your service list is ready for clients to explore!",
                isDone: servicesConfigured,
                onClick: () => navigate("/menu/services"),
            },
            {
                key: "location",
                label: "Set working hours",
                explainText:
                    "Show when you're available so clients can book you at the perfect time.",
                successText: "Your schedule is ready for booking!",
                isDone: locationConfigured,
                onClick: () => navigate("/menu/locations"),
            },
            {
                key: "profile",
                label: "Create taplink profile",
                explainText:
                    "Add your contact details and links so clients can reach you anytime.",
                successText: "Your profile is now live and looking great!",
                isDone: profileConfigured,
                onClick: () => navigate("/menu/profile"),
            },
            {
                key: "loyalty",
                label: "Set up loyalty program",
                explainText:
                    "Launch referral rewards and subscriptions to attract new clients and keep regulars coming back.",
                successText:
                    "Your loyalty program is live and ready to grow your client base!",
                isDone: loyaltyConfigured,
                onClick: () => navigate("/menu/loyalty"),
            },
        ],
        [
            brandConfigured,
            wallpaperConfigured,
            servicesConfigured,
            locationConfigured,
            profileConfigured,
            loyaltyConfigured,
            navigate,
        ]
    );

    const firstUndoneIndex = useMemo(() => {
        const idx = ONBOARDING_STEPS.findIndex((s) => !s.isDone);
        return idx === -1 ? 0 : idx;
    }, [ONBOARDING_STEPS]);

    const [emblaRef, emblaApi] = useEmblaCarousel({
        containScroll: "keepSnaps",
        align: "center",
        dragFree: false,
        loop: false,
        slidesToScroll: 1,
        startIndex: firstUndoneIndex,
    });

    // If props arrive after Embla init, scroll once to the right slide
    const hasAutoScrolled = useRef(false);
    useEffect(() => {
        if (emblaApi && !hasAutoScrolled.current) {
            emblaApi.scrollTo(firstUndoneIndex);
            hasAutoScrolled.current = true;
        }
    }, [emblaApi, firstUndoneIndex]);

    // --- Opacity logic (unchanged) ---
    const [slideOpacity, setSlideOpacity] = useState<number[]>(
        ONBOARDING_STEPS.map(() => 0.4)
    );
    const updateSlideStyles = useCallback(() => {
        if (!emblaApi) return;
        const selectedIndex = emblaApi.selectedScrollSnap();
        const styles = emblaApi.slideNodes().map((_, index) => {
            const distance = Math.abs(selectedIndex - index);
            return Math.max(0.2, 1 - distance * 0.4);
        });
        setSlideOpacity(styles);
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        updateSlideStyles();
        emblaApi.on("scroll", updateSlideStyles);
        emblaApi.on("reInit", updateSlideStyles);
        emblaApi.on("select", updateSlideStyles);
        return () => {
            emblaApi.off("scroll", updateSlideStyles);
            emblaApi.off("reInit", updateSlideStyles);
            emblaApi.off("select", updateSlideStyles);
        };
    }, [emblaApi, updateSlideStyles]);

    // --- Dots state (unchanged logic) ---
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(firstUndoneIndex);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    const onReInit = useCallback(() => {
        if (!emblaApi) return;
        setScrollSnaps(emblaApi.scrollSnapList());
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onReInit();
        emblaApi.on("select", onSelect);
        emblaApi.on("reInit", onReInit);
        return () => {
            emblaApi.off("select", onSelect);
            emblaApi.off("reInit", onReInit);
        };
    }, [emblaApi, onReInit, onSelect]);

    const scrollTo = (index: number) => emblaApi?.scrollTo(index);

    const doneCount = ONBOARDING_STEPS.filter((s) => s.isDone).length;

    return (
        <Paper
            elevation={0}
            sx={{
                width: "100%",
                boxSizing: "border-box",
                overflowX: "clip",
                borderRadius: CARD_STYLES.borderRadius,
                boxShadow: CARD_STYLES.boxShadow,
                py: 1,
                bgcolor: CARD_STYLES.bgcolor,
            }}
        >
            <Stack alignItems="center" spacing={0.5} width="100%">
                <Typography
                    fontSize="12px"
                    color="text.secondary"
                    sx={{ pt: 0.5 }}
                >
                    {doneCount} of {ONBOARDING_STEPS.length} steps completed
                </Typography>
                {/* Carousel viewport */}
                <Box
                    ref={emblaRef}
                    sx={{
                        overflow: "hidden",
                        width: "100%",
                        height: "clamp(200px, 55vw, 260px)",
                        borderRadius: "12px",
                        px: 1.5,
                        boxSizing: "border-box",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            height: "100%",
                            gap: 1.5,
                            boxSizing: "border-box",
                        }}
                    >
                        {ONBOARDING_STEPS.map((step, index) => (
                            <Card
                                key={step.key}
                                sx={{
                                    flex: "0 0 80%",
                                    width: "40%",
                                    transition: "opacity 0.3s ease",
                                    opacity: slideOpacity[index] ?? 0.4,
                                    bgcolor: step.isDone
                                        ? "white"
                                        : theme.palette.primary.main,
                                    border: `2px solid ${theme.palette.primary.main}`,
                                    borderRadius: "14px",
                                    boxShadow:
                                        "0px 4px 17px rgba(0, 0, 0, 0.04)",
                                    p: 1.5,
                                    height: "80%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    //mx: 1.5,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: step.isDone
                                                ? theme.palette.primary.main
                                                : "white",
                                            width: 28,
                                            height: 28,
                                            fontSize: "13px",
                                            fontWeight: 600,
                                            color: step.isDone
                                                ? "white"
                                                : theme.palette.primary.main,
                                        }}
                                    >
                                        {step.isDone ? (
                                            <CheckCircleIcon width={28} />
                                        ) : (
                                            index + 1
                                        )}
                                    </Avatar>
                                    <Typography
                                        fontWeight={600}
                                        fontSize="15px"
                                        color={
                                            step.isDone
                                                ? "text.primary"
                                                : "white"
                                        }
                                        mb={1}
                                    >
                                        {step.label}
                                    </Typography>
                                </Stack>

                                <Typography
                                    fontSize="clamp(11px, 3vw, 15px)"
                                    lineHeight={1.4}
                                    color={
                                        step.isDone ? "text.secondary" : "white"
                                    }
                                >
                                    {step.isDone
                                        ? step.successText
                                        : step.explainText}
                                </Typography>

                                <Button
                                    variant="small"
                                    fullWidth
                                    onClick={step.onClick}
                                    color={
                                        step.isDone ? "primary" : "secondary"
                                    }
                                    textColor={
                                        step.isDone
                                            ? "white"
                                            : theme.palette.primary.main
                                    }
                                    sx={{
                                        "&:hover": {
                                            bgcolor: step.isDone
                                                ? theme.palette.primary.dark
                                                : "rgba(255,255,255,0.9)",
                                        },
                                    }}
                                >
                                    {step.isDone
                                        ? "Edit Details"
                                        : `${step.label}`}
                                </Button>
                            </Card>
                        ))}
                    </Box>
                </Box>
                {/* Dots — smaller and closer */}
                {scrollSnaps.length > 1 && (
                    <Stack
                        direction="row"
                        spacing={0.75} // CHANGED: tighter spacing between dots
                        justifyContent="center"
                        sx={{ mt: 0.75 }} // CHANGED: closer to cards
                    >
                        {scrollSnaps.map((_, i) => {
                            const active = i === selectedIndex;
                            return (
                                <Box
                                    key={i}
                                    component="button"
                                    onClick={() => scrollTo(i)}
                                    aria-label={`Go to slide ${i + 1}`}
                                    aria-current={active ? "true" : undefined} // NEW: a11y hint
                                    sx={{
                                        all: "unset",
                                        cursor: "pointer",
                                        width: 8, // CHANGED: smaller dot
                                        height: 8, // CHANGED: smaller dot
                                        borderRadius: "50%",
                                        border: `2px solid ${theme.palette.primary.main}`,
                                        backgroundColor: active
                                            ? theme.palette.primary.main
                                            : "transparent",
                                        transition:
                                            "background-color 0.2s ease, transform 0.12s ease",
                                        "&:hover": { transform: "scale(1.08)" }, // tiny affordance
                                    }}
                                />
                            );
                        })}
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
};

export default SetupWizard;
